import { useEffect, useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Save, RotateCcw, SlidersHorizontal, X } from "lucide-react";
import { toast } from "sonner";
import { saveRunParams } from "../../api/settings";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { defaultRunParams } from "../../lib/run-params";
import type { RunParams } from "../../lib/types";

export function RunParamsDialog({
  open,
  initialParams,
  overrideParams,
  onClose,
  onSaved,
  onApplyOverride,
  onClearOverride
}: {
  open: boolean;
  initialParams: RunParams;
  overrideParams?: RunParams | null;
  onClose: () => void;
  onSaved: (params: RunParams) => void;
  onApplyOverride: (params: RunParams) => void;
  onClearOverride: () => void;
}) {
  const [params, setParams] = useState<RunParams>(defaultRunParams);
  const saveMutation = useMutation({
    mutationFn: saveRunParams,
    onSuccess: ({ run_params }) => {
      setParams(run_params);
      onSaved(run_params);
      toast.success("默认执行参数已保存");
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "保存失败")
  });

  useEffect(() => {
    if (open) {
      const source = overrideParams || initialParams;
      setParams(source);
    }
  }, [initialParams, open, overrideParams]);

  const preview = useMemo(() => {
    return JSON.stringify(params, null, 2);
  }, [params]);
  if (!open) return null;

  function save() {
    const next = parseDraftParams();
    if (!next) return;
    saveMutation.mutate(next);
  }

  function parseDraftParams(): RunParams | null {
    return { ...params, proxy_params: {} };
  }

  function discard() {
    const source = overrideParams || initialParams;
    setParams(source);
    onClose();
  }

  function applyOverride() {
    const next = parseDraftParams();
    if (!next) return;
    onApplyOverride(next);
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-950/30 p-4">
      <div className="w-full max-w-3xl rounded-lg bg-white shadow-xl">
        <div className="flex h-12 items-center justify-between border-b px-4">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold">执行参数</h2>
            {overrideParams ? <span className="rounded bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700">本次覆盖中</span> : null}
          </div>
          <Button onClick={onClose}>
            <X size={14} />
          </Button>
        </div>
        <div className="grid max-h-[75vh] gap-4 overflow-auto p-4 md:grid-cols-[1fr_1fr]">
          <section className="space-y-3">
            <h3 className="text-xs font-semibold text-slate-500">默认执行参数</h3>
            {[
              ["use_fingerprint", "使用 CloakBrowser profile"],
              ["sync_to_feishu", "同步飞书"],
              ["same_profile_cache_warmup_enabled", "缓存预热"],
              ["force_new_proxy", "强制新代理"]
            ].map(([field, label]) => (
              <label key={field} className="flex items-center justify-between rounded border px-3 py-2 text-xs">
                {label}
                <input
                  type="checkbox"
                  checked={Boolean(params[field])}
                  onChange={(event) => setParams((current) => ({ ...current, [field]: event.target.checked }))}
                />
              </label>
            ))}
            <label className="grid gap-1 text-xs">
              代理重试次数
              <Input
                type="number"
                min={1}
                value={params.proxy_retry_count}
                onChange={(event) => setParams((current) => ({ ...current, proxy_retry_count: Number(event.target.value) || 1 }))}
              />
            </label>
            <div className="space-y-1 rounded-md border border-slate-200 bg-slate-50 p-2 text-xs text-slate-600">
              <p>保存默认参数：写入 D1 的 app_config，后续任务默认使用。</p>
              <p>应用本次覆盖：只影响当前页面后续行内/批量任务，不写入 app_config。</p>
              <p>代理由账号当前代理或运行配置里的默认代理商决定，不再从这里填写 JSON。</p>
            </div>
          </section>
          <section className="space-y-3">
            <h3 className="text-xs font-semibold text-slate-500">实际提交预览</h3>
            <pre className="h-80 overflow-auto rounded-md bg-slate-950 p-3 text-xs text-slate-50">{preview}</pre>
          </section>
        </div>
        <div className="flex justify-end gap-2 border-t p-3">
          <Button onClick={discard}>
            <X size={14} />
            放弃修改
          </Button>
          {overrideParams ? (
            <Button
              onClick={() => {
                onClearOverride();
                onClose();
              }}
            >
              <X size={14} />
              清除本次覆盖
            </Button>
          ) : null}
          <Button onClick={applyOverride}>
            <SlidersHorizontal size={14} />
            应用本次覆盖
          </Button>
          <Button
            onClick={() => {
              setParams(defaultRunParams);
              toast.info("已恢复系统默认，保存后生效");
            }}
          >
            <RotateCcw size={14} />
            恢复系统默认
          </Button>
          <Button disabled={saveMutation.isPending} onClick={save} className="bg-slate-900 text-white hover:bg-slate-800">
            <Save size={14} />
            保存默认参数
          </Button>
        </div>
      </div>
    </div>
  );
}
